/**
 * HardwareStatusIndicator - Pastilles colorees montrant l'etat du bridge, balance et imprimante
 * Tap pour voir les details (pas de hover/tooltip - tactile uniquement)
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useHardwareStatus } from '../../hooks/useHardwareStatus';

export default function HardwareStatusIndicator() {
  const { bridgeConnected, scaleConnected, epsonAvailable, posId, ssid, networkLabel, ip, rssi, certDaysLeft, certExpiresAt, restartBridge } = useHardwareStatus();

  // Certificat du bridge : Apple plafonne la validite a 825 jours, celui-ci est
  // emis pour 800. S'il expire, TOUT le materiel tombe d'un coup — balance,
  // tickets, etiquettes, tiroir — avec pour seul symptome « bridge
  // indisponible ». On previent donc 60 jours avant, la ou l'incident se
  // produira. Renouvellement : voir docs (regenerer avec SAN + 800 jours).
  const certExpiringSoon = certDaysLeft !== null && certDaysLeft <= 60;

  // Qualité du lien Wi-Fi. Seuils usuels : au-delà de -60 dBm c'est confortable,
  // -70 reste utilisable, en dessous les coupures deviennent fréquentes.
  const wifiTone =
    rssi === null ? 'bg-gray-400' : rssi >= -60 ? 'bg-green-500' : rssi >= -70 ? 'bg-amber-500' : 'bg-red-500';
  const [showDetails, setShowDetails] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const handleRestart = async () => {
    setRestarting(true);
    setShowDetails(false);
    toast.info('Redemarrage du bridge...');

    const success = await restartBridge();
    if (success) {
      // Le bridge va se couper puis revenir — le poll detectera le changement
      setTimeout(() => {
        setRestarting(false);
        toast.success('Bridge redemarre');
      }, 4000);
    } else {
      setRestarting(false);
      toast.error('Impossible de joindre le bridge');
    }
  };

  return (
    <div className="relative">
      {/* Pastilles compactes */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center gap-2 px-2 py-1.5 bg-gray-100 rounded-lg active:bg-gray-200 transition-colors"
      >
        <span className="flex items-center gap-1 text-sm">
          <span>⚖️</span>
          <span className={`w-2.5 h-2.5 rounded-full ${scaleConnected ? 'bg-green-500' : 'bg-red-500'}`} />
        </span>
        <span className="flex items-center gap-1 text-sm">
          <span>🖨️</span>
          <span className={`w-2.5 h-2.5 rounded-full ${epsonAvailable ? 'bg-green-500' : 'bg-red-500'}`} />
        </span>
        {networkLabel && (
          <span className="flex items-center gap-1 text-sm max-w-[140px]">
            <span>📶</span>
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${wifiTone}`} />
            <span className="text-xs text-gray-700 truncate" title={ssid || networkLabel || undefined}>{networkLabel}</span>
          </span>
        )}
        {certExpiringSoon && (
          <span
            className="text-xs text-amber-700 font-medium bg-amber-100 px-1.5 py-0.5 rounded"
            title={`Le certificat du bridge expire dans ${certDaysLeft} jours. Sans renouvellement, balance, tickets, etiquettes et tiroir cesseront de fonctionner sur cette caisse.`}
          >
            🔐 Certificat {certDaysLeft} j
          </span>
        )}
        {!bridgeConnected && (
          <span className="text-xs text-red-600 font-medium">
            {restarting ? '...' : 'Bridge off'}
          </span>
        )}
      </button>

      {/* Panneau de details (tap pour ouvrir/fermer) */}
      {showDetails && (
        <>
          {/* Backdrop pour fermer au tap */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDetails(false)}
          />
          <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4 w-64">
            <h3 className="font-bold text-sm text-gray-900 mb-3">Peripheriques</h3>

            <div className="space-y-2">
              {/* Bridge */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">🔌 Bridge</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  bridgeConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {bridgeConnected ? 'Connecte' : 'Deconnecte'}
                </span>
              </div>

              {/* Balance */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">⚖️ Balance</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  scaleConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {scaleConnected ? 'Connectee' : 'Deconnectee'}
                </span>
              </div>

              {/* Imprimante */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">🖨️ EPSON</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  epsonAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {epsonAvailable ? 'Disponible' : 'Indisponible'}
                </span>
              </div>

              {/* Réseau — sur quel Wi-Fi la caisse est branchée */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">📶 Wi-Fi</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  networkLabel ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {networkLabel || 'Non connecte'}
                </span>
              </div>
              {networkLabel && (
                <div className="text-xs text-gray-500 -mt-1">
                  {rssi !== null && <span>Signal {rssi} dBm</span>}
                  {rssi !== null && ip && <span> · </span>}
                  {ip && <span>{ip}</span>}
                </div>
              )}

              {/* Certificat du bridge — affiche en permanence : c'est la seule
                  trace visible d'une echeance lointaine (2028). Passe en orange
                  a 60 jours, en rouge une fois depasse. */}
              {certExpiresAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">🔐 Certificat</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    certDaysLeft !== null && certDaysLeft <= 0 ? 'bg-red-100 text-red-800'
                      : certExpiringSoon ? 'bg-amber-100 text-amber-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {new Date(certExpiresAt).toLocaleDateString('fr-BE')}
                    {certDaysLeft !== null && ` · ${certDaysLeft} j`}
                  </span>
                </div>
              )}

              {/* POS ID */}
              {posId && (
                <div className="pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-500">POS: {posId}</span>
                </div>
              )}

              {/* Bouton Redémarrer */}
              <div className="pt-2 border-t border-gray-100">
                <button
                  onClick={handleRestart}
                  disabled={!bridgeConnected || restarting}
                  className="w-full py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 active:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  🔄 {restarting ? 'Redemarrage...' : 'Redemarrer le bridge'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
